import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Switch } from '@mui/material';
import { Autocomplete, TextField, Typography } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { get, set } from '@totalsoft/change-tracking-react';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';

export const FinancialProductGeneralDataComponent = ({
  canWrite,
  financialProduct,
  contractTypes,
  leasingPromotions,
  currencies,
  scheduleTypes,
  leasingIncomeSchemas,
  leasingInterestTypes,
  validation,
  productId
}) => {
  const [t] = useTranslation();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant='h6'>{t('FinancialProduct.DescriptionAndUse')}</Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={(financialProduct.productName |> get) || ''}
          onChange={financialProduct.productName |> set}
          label={t('FinancialProduct.ProductName')}
          error={!isValid(validation.productName)}
          helperText={getErrors(validation.productName)}
          disabled={!canWrite}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={(financialProduct.productCode |> get) || ''}
          onChange={financialProduct.productCode |> set}
          label={t('FinancialProduct.ProductCode')}
          error={!isValid(validation.productCode)}
          helperText={getErrors(validation.productCode)}
          disabled={!canWrite}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <DateTime
          name='validFrom'
          timeFormat={false}
          value={financialProduct.leasingFinancialProduct.validFrom |> get}
          label={t('FinancialProduct.ValidFrom')}
          onChange={financialProduct.leasingFinancialProduct.validFrom |> set}
          error={!isValid(validation.leasingFinancialProduct.validFrom)}
          helperText={getErrors(validation.leasingFinancialProduct.validFrom)}
          disabled={!canWrite}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <DateTime
          name='validTo'
          timeFormat={false}
          value={financialProduct.leasingFinancialProduct.validTo |> get}
          label={t('FinancialProduct.ValidTo')}
          onChange={financialProduct.leasingFinancialProduct.validTo |> set}
          error={!isValid(validation.leasingFinancialProduct.validTo)}
          helperText={getErrors(validation.leasingFinancialProduct.validTo)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={(financialProduct.description |> get) || ''}
          onChange={financialProduct.description |> set}
          label={t('FinancialProduct.Description')}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={1}>
        <FormControlLabel
          control={<Switch checked={(financialProduct.isActive |> get) || false} />}
          label={t('FinancialProduct.IsActive')}
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} sm={1}>
        <FormControlLabel
          control={<Switch checked={(financialProduct.leasingFinancialProduct.isSpecialAction |> get) || false} />}
          label={t('FinancialProduct.IsSpecialAction')}
          onChange={financialProduct.leasingFinancialProduct.isSpecialAction |> set |> onCheckBoxChange}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant='h6'>{t('FinancialProduct.ValuesAndConstraints')}</Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          value={financialProduct.leasingFinancialProduct.leasingTypeId |> get}
          simpleValue
          valueKey='contractTypeId'
          labelKey='contractTypeName'
          options={contractTypes}
          label={t('FinancialProduct.LeasingTypeName')}
          onChange={financialProduct.leasingFinancialProduct.leasingTypeId |> set}
          isSearchable={true}
          error={!isValid(validation.leasingFinancialProduct.leasingTypeId)}
          helperText={getErrors(validation.leasingFinancialProduct.leasingTypeId)}
          disabled={!canWrite}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          value={financialProduct.leasingFinancialProduct.promoId |> get}
          simpleValue
          valueKey='promoId'
          labelKey='promoName'
          options={leasingPromotions}
          label={t('FinancialProduct.Promotion')}
          onChange={financialProduct.leasingFinancialProduct.promoId |> set}
          isSearchable={true}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          value={financialProduct.leasingFinancialProduct.creditCurrencyId |> get}
          simpleValue
          valueKey='id'
          labelKey='code'
          options={currencies}
          label={t('FinancialProduct.Currency')}
          onChange={financialProduct.leasingFinancialProduct.creditCurrencyId |> set}
          isSearchable={true}
          error={!isValid(validation.leasingFinancialProduct.creditCurrencyId)}
          helperText={getErrors(validation.leasingFinancialProduct.creditCurrencyId)}
          disabled={!canWrite}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          value={financialProduct.leasingFinancialProduct.scheduleTypeId |> get}
          simpleValue
          valueKey='scheduleTypeId'
          labelKey='scheduleTypeName'
          options={scheduleTypes}
          label={t('FinancialProduct.PaymentPlanType')}
          onChange={data => {
            if (productId === null || !(financialProduct.hasSeasonalityTemplates |> get)) {
              set(financialProduct.leasingFinancialProduct.scheduleTypeId)(data);
            } else {
              if (window.confirm(t('FinancialProduct.Details.SeasonalityTemplates.ThisActionWillDelete'))) {
                set(financialProduct.leasingFinancialProduct.scheduleTypeId)(data);
              }
            }
          }}
          isSearchable={true}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          value={financialProduct.leasingFinancialProduct.leasingIncomeSchemaId |> get}
          simpleValue
          valueKey='schemaId'
          labelKey='name'
          options={leasingIncomeSchemas}
          label={t('FinancialProduct.LeasingIncomeSchema')}
          onChange={financialProduct.leasingFinancialProduct.leasingIncomeSchemaId |> set}
          isSearchable={true}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          value={financialProduct.leasingFinancialProduct.interestTypeId |> get}
          simpleValue
          valueKey='interestTypeId'
          labelKey='interestTypeName'
          options={leasingInterestTypes}
          label={t('FinancialProduct.LeasingInterestType')}
          onChange={financialProduct.leasingFinancialProduct.interestTypeId |> set}
          isSearchable={true}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          required
          value={(financialProduct.leasingFinancialProduct.paymentFrequency |> get) || ''}
          onChange={financialProduct.leasingFinancialProduct.paymentFrequency |> set}
          label={t('FinancialProduct.PaymentFrequency')}
          disabled={!canWrite}
          error={!isValid(validation.leasingFinancialProduct.paymentFrequency)}
          helperText={getErrors(validation.leasingFinancialProduct.paymentFrequency)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          required
          value={(financialProduct.leasingFinancialProduct.contractDuration |> get) || ''}
          onChange={financialProduct.leasingFinancialProduct.contractDuration |> set}
          label={t('FinancialProduct.ContractDuration')}
          disabled={!canWrite}
          error={!isValid(validation.leasingFinancialProduct.contractDuration)}
          helperText={getErrors(validation.leasingFinancialProduct.contractDuration)}
        />
      </Grid>
    </Grid>
  );
};

FinancialProductGeneralDataComponent.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  financialProduct: PropTypes.object.isRequired,
  contractTypes: PropTypes.array.isRequired,
  leasingPromotions: PropTypes.array.isRequired,
  currencies: PropTypes.array.isRequired,
  scheduleTypes: PropTypes.array.isRequired,
  leasingIncomeSchemas: PropTypes.array.isRequired,
  leasingInterestTypes: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired,
  productId: PropTypes.number
};

export default FinancialProductGeneralDataComponent;
